import { ClearOutlined, FilterOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, notification, Spin, Tooltip } from 'antd'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { QUERY_KEYS } from '@api'
import { DeleteTicketModal } from '@components'
import { useFilters, useGeneral, useUser } from '@contexts'
import { ImplementationTimeUtils } from '@utils'
import {
  useCarriers,
  useCreateTicket,
  useDeleteTicket,
  useEquipment,
  useGetTickets,
  useMaterials,
  useSuppliers,
  useSuppliersZones,
  useTickets,
} from '@hooks'

import { useSocket } from '../../../../hooks/useSocket'
import RangeTimeSwitcher from '../../../../components/RangeTimeSwitcher'
import {
  ContentContainer,
  StyledButton,
  StyledRow,
  StyledSelect,
} from '../../../../components/StyledComponents'
import { useHandleDeleteTicketModal } from '../../../../contexts/HandleDeleteTicketModalContext'
import { useHandleTicketModal } from '../../../../contexts/HandleTicketModalContext'
import {
  CommonTicketForm,
  DayToPushProps,
  Filters,
  RoleEnum,
  StatusTicketEnum,
  Ticket,
  TripTypeEnum,
} from '../../../../types'
import { AddTicketModalAdmin } from '../../AddTicketModalAdmin'
import { useApproveTicket } from '../../AdminDashboard/hooks'
import UpdateTicketModalAdmin from '../../UpdateTicketModalAdmin'
import RequestsBoard from '../components/RequestsBoard'

type ModalAction = 'update' | 'add'

dayjs.extend(isoWeek)

const { inImplementationZone } = ImplementationTimeUtils

const { ADMIN } = RoleEnum

const Ongoing = () => {
  const { t } = useTranslation()
  const { roles, state } = useUser()
  const gmtZone = state?.company?.gmtZone ?? 0
  const queryClient = useQueryClient()
  const { filters, setFilters, resetFilters } = useFilters()
  const { changeTicket, changeTicketLoading } = useTickets()
  const { tickets, ticketsIsLoading } = useGetTickets(filters)

  // WebSocket connection and ticket updates
  const { connect, socket } = useSocket()

  // Connect to WebSocket when component mounts
  useEffect(() => {
    connect()
  }, [connect])

  // Escuchar actualizaciones de tickets directamente en el socket
  useEffect(() => {
    if (!socket) return

    const handleTicketUpdate = () => {
      queryClient.invalidateQueries([QUERY_KEYS.TICKETS])
      queryClient.invalidateQueries([QUERY_KEYS.TICKET])
      queryClient.invalidateQueries([QUERY_KEYS.TO_AUTHORIZE])
    }

    socket.on('ticket-update', handleTicketUpdate)

    return () => {
      socket.off('ticket-update', handleTicketUpdate)
    }
  }, [socket, queryClient])

  const {
    suppliersOptions,
    isLoading: isSuppliersLoading,
    suppliers,
  } = useSuppliers({ disabled: false })
  const { suppliersZones, isLoading: isSuppliersZonesLoading } = useSuppliersZones()
  const { equipmentOptions, isLoading: isEquipmentLoading } = useEquipment()
  const { createTicket, isLoading: isCreateTicketLoading } = useCreateTicket()
  const { ticketToEdit, setTicketToEdit, setIsTicketModalOpen } = useHandleTicketModal()
  const { approveTicket } = useApproveTicket()
  const { allCarriersOptions, isLoadingAllCarriers } = useCarriers({ disabled: false, limit: 400 })
  const isAdmin = roles?.includes(ADMIN)
  const {
    setTicketToSearch,
    ticketToSearch,
    filtersDisplayed,
    handleDisplayFilters,
    menuDisplayed,
  } = useGeneral()
  const { isDeleteTicketModalOpen, setIsDeleteTicketModalOpen } = useHandleDeleteTicketModal()
  const { isLoading: isDeleteTicketLoading } = useDeleteTicket()
  const { materialsOptions } = useMaterials()
  const [openModal, setOpenModal] = useState<ModalAction>()
  const [pendingStatusName, setPendingStatusName] = useState<string[]>(filters?.statusName ?? [])

  useEffect(() => {
    if (ticketToSearch && tickets?.total === 1) {
      const [ticket] = tickets?.tickets
      if (ticket) {
        setFilters((prevState: Filters) => ({
          ...prevState,
          mondayOfWeek: ImplementationTimeUtils.companyStartOfIsoWeek(
            ticket.withdrawalDate,
            gmtZone,
          ).toDate(),
        }))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets])

  useEffect(() => {
    setPendingStatusName(filters?.statusName ?? [])
  }, [filters?.statusName])

  const submitHandler = (
    id: number | undefined,
    form: CommonTicketForm,
    resetForm: () => void,
    dates: Date[] | undefined,
    toApproved?: boolean,
  ) => {
    if (id) {
      changeTicket(
        {
          id,
          payload: {
            ...form,
            ticketDate: dates![0],
          },
        },
        {
          onSuccess: () => {
            notification.success({
              message: `${t(
                'admin_router.admin_dashboard.use_approve_tickets.edit_success',
              )} ${id}`,
            })
            if (toApproved) {
              approveTicket({ id, note: form.note, type: form.type })
            }
            resetForm()
            // Cerrar el modal solo cuando la operación es exitosa
            setTicketToEdit(0)
            setIsTicketModalOpen(false)
          },
          onError: () => {
            // Cerrar el modal cuando hay un error (igual que carrier)
            setTicketToEdit(0)
            setIsTicketModalOpen(false)
            // El error ya se maneja en el hook useTickets
          },
        },
      )
    } else {
      createTicket(
        {
          ...form,
          ticketsDate: dates,
        },
        {
          onSuccess: () => {
            resetForm()
            // Cerrar el modal solo cuando la operación es exitosa
            setTicketToEdit(0)
            setIsTicketModalOpen(false)
          },
          onError: () => {
            // Cerrar el modal cuando hay un error (igual que carrier)
            setTicketToEdit(0)
            setIsTicketModalOpen(false)
          },
        },
      )
    }
  }

  const [ticketsByDay, setTicketsByDay] = useState<DayToPushProps[]>([])

  const daysOfTheWeek = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo']

  const days: string[] = []
  daysOfTheWeek.forEach((_day, index) => {
    days.push(
      inImplementationZone(filters?.mondayOfWeek, gmtZone).add(index, 'days').format('dddd DD-MM'),
    )
  })

  const capitalize = (string: string) => string.charAt(0).toUpperCase() + string.slice(1)

  const filterOptions = [
    {
      label: `${t('admin_router.filter_options.status.created')}`,
      value: StatusTicketEnum.CREATED,
    },
    {
      label: `${t('admin_router.filter_options.status.pending')}`,
      value: StatusTicketEnum.PENDING,
    },
    {
      label: `${t('admin_router.filter_options.status.confirmed')}`,
      value: StatusTicketEnum.CONFIRMED,
    },
    {
      label: `${t('admin_router.filter_options.status.supplier_invoiced')}`,
      value: StatusTicketEnum.TRANSIT,
    },
    {
      label: `${t('tpl_router.filterOptions.delivered')}`,
      value: StatusTicketEnum.DELIVERED,
    },
    {
      label: `${t('globals.generics.status.no_show')}`,
      value: StatusTicketEnum.NO_SHOW,
    },
    {
      label: `${t('admin_router.filter_options.status.canceled')}`,
      value: StatusTicketEnum.CANCELED,
    },
  ]

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFilterOptions = (input: string, option: any) => {
    if (option.label) return option.label.toString().toLowerCase().includes(input.toLowerCase())
    return true
  }

  const handleStatusFilterChange = (value: any) => {
    setPendingStatusName([...(value as string[])])
  }

  const handleApplyStatusFilter = () => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      statusName: [...pendingStatusName],
      ticketId: undefined,
    }))
  }

  const handleSupplierFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      supplierId: [...(value as number[])],
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }

  const handleSupplierZoneFilterChange = (values: any) => {
    const valuesAux = values as string[]
    const suppliersFilters: number[] = []
    valuesAux.forEach(value => {
      const suppliersAux = suppliers?.filter(supplier => supplier.zone === value)
      if (suppliersAux) {
        suppliersAux.forEach(supplierAux => {
          suppliersFilters.push(supplierAux.id)
        })
      }
    })

    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      supplierId: suppliersFilters,
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }
  const handleEquipmentFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      equipmentId: [...(value as number[])],
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }
  const handleTripTypeFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      tripType: [...(value as string[])],
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }

  const handleCarrierFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      carrierId: [...(value as number[])],
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }
  const handleMaterialFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      materialId: [...(value as number[])],
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }
  const handleCreatedByFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      createdByMe: value === 'me' ? true : undefined,
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }
  const handleCleanFilters = () => {
    setTicketToSearch(undefined)
    setPendingStatusName([])
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      carrierId: [],
      carrierMode: [],
      statusName: [],
      supplierId: [],
      materialId: [],
      equipmentId: [],
      orderBy: undefined,
      ticketId: undefined,
      zone: [],
      createdByMe: undefined,
    }))
    resetFilters()
    queryClient.resetQueries(QUERY_KEYS.TICKETS)
  }

  const handleOpenAddTicketModal = () => {
    setTicketToEdit(0)
    setOpenModal('add')
  }
  const handleCloseAddTicketModal = () => {
    setOpenModal(undefined)
  }
  const handleCloseTicketModal = () => {
    setIsTicketModalOpen(false)
  }

  const handleCleanSearchInput = () => {
    setTicketToSearch(undefined)
  }

  useEffect(() => {
    if (tickets?.tickets) {
      const ticketsToSet: DayToPushProps[] = []
      days.forEach((day: string) => {
        const dayToPush = {
          date: capitalize(day),
          tickets: tickets?.tickets
            .filter(
              (ticket: Ticket) =>
                inImplementationZone(ticket.withdrawalDate, gmtZone).format('dddd DD-MM') === day,
            )
            .sort((a, b) => {
              const diff =
                new Date(a.withdrawalDate).getTime() - new Date(b.withdrawalDate).getTime()
              if (diff === 0) {
                return a.id - b.id
              }
              return diff
            }),
        }
        ticketsToSet.push(dayToPush)
      })
      setTicketsByDay(ticketsToSet)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets?.tickets, filters?.mondayOfWeek, gmtZone])

  return (
    <>
      <ContentContainer className="admin-dashboard">
        <StyledRow className="sticky-options">
          {roles?.includes(ADMIN) && (
            <>
              <Tooltip
                placement="bottomRight"
                title={t('suppliers_router.new_ticket_modal.tooltip')}
              >
                <StyledButton className="inline" onClick={handleOpenAddTicketModal}>
                  <PlusOutlined className="option-button-icon" />
                </StyledButton>
              </Tooltip>
            </>
          )}

          <div
            className={`filters-container${
              filtersDisplayed && !menuDisplayed ? ' filters-displayed' : ''
            }`}
          >
            <Button onClick={handleDisplayFilters} id="displayFiltersButton">
              <FilterOutlined />
            </Button>
            <div className="filters-list">
              <Tooltip placement="bottomRight" title={t('globals.generics.clean_filters')}>
                <StyledButton
                  className="inline option"
                  onClick={handleCleanFilters}
                  icon={<ClearOutlined className="option-button-icon" />}
                />
              </Tooltip>
              <div className="status-filter-apply-wrap">
                <StyledSelect
                  mode="multiple"
                  allowClear
                  placeholder={t('admin_router.filter_options.status.title')}
                  options={filterOptions}
                  onChange={handleStatusFilterChange}
                  filterOption={handleFilterOptions}
                  value={pendingStatusName}
                  onDropdownVisibleChange={handleCleanSearchInput}
                  maxTagCount="responsive"
                  className="status-filter-apply-select"
                />
                <div
                  className="status-filter-apply-trigger"
                  role="button"
                  tabIndex={0}
                  onClick={handleApplyStatusFilter}
                  onKeyDown={e => e.key === 'Enter' && handleApplyStatusFilter()}
                  aria-label={t('globals.generics.apply.title')}
                >
                  <SearchOutlined />
                </div>
              </div>
              <StyledSelect
                mode="multiple"
                allowClear
                placeholder={t('admin_router.filter_options.zone')}
                options={suppliersZones}
                onChange={handleSupplierZoneFilterChange}
                filterOption={handleFilterOptions}
                loading={isSuppliersZonesLoading}
                value={filters?.zone}
                onDropdownVisibleChange={handleCleanSearchInput}
                maxTagCount="responsive"
              />
              <StyledSelect
                mode="multiple"
                allowClear
                maxTagCount="responsive"
                placeholder={t('admin_router.filter_options.supplier')}
                options={suppliersOptions}
                filterOption={handleFilterOptions}
                onChange={handleSupplierFilterChange}
                loading={isSuppliersLoading}
                value={filters?.supplierId}
                onDropdownVisibleChange={handleCleanSearchInput}
              />
              <StyledSelect
                mode="multiple"
                allowClear
                placeholder={t('admin_router.filter_options.by_carrier.title')}
                options={allCarriersOptions}
                loading={isLoadingAllCarriers}
                filterOption={handleFilterOptions}
                onChange={handleCarrierFilterChange}
                value={filters?.carrierId}
                onDropdownVisibleChange={handleCleanSearchInput}
              />
              <StyledSelect
                maxTagCount="responsive"
                mode="multiple"
                allowClear
                placeholder={t('suppliers_router.filter_options.material.title')}
                options={materialsOptions}
                filterOption={handleFilterOptions}
                onChange={handleMaterialFilterChange}
                value={filters?.materialId}
                onDropdownVisibleChange={handleCleanSearchInput}
              />
              <StyledSelect
                mode="multiple"
                allowClear
                placeholder={t('admin_router.filter_options.equipment')}
                options={equipmentOptions}
                filterOption={handleFilterOptions}
                onChange={handleEquipmentFilterChange}
                loading={isEquipmentLoading}
                value={filters?.equipmentId}
                onDropdownVisibleChange={handleCleanSearchInput}
              />
              <StyledSelect
                mode="multiple"
                allowClear
                placeholder={t('admin_router.filter_options.trip_type')}
                options={Object.values(TripTypeEnum).map(type => ({
                  label: type,
                  value: type,
                }))}
                onChange={handleTripTypeFilterChange}
                filterOption={handleFilterOptions}
                value={filters?.tripType}
                onDropdownVisibleChange={handleCleanSearchInput}
                maxTagCount="responsive"
              />
              {isAdmin && (
                <StyledSelect
                  allowClear
                  placeholder={t('admin_router.filter_options.created_by')}
                  options={[
                    {
                      label: t('admin_router.filter_options.created_by_options.all'),
                      value: 'all',
                    },
                    { label: t('admin_router.filter_options.created_by_options.me'), value: 'me' },
                  ]}
                  onChange={handleCreatedByFilterChange}
                  value={filters?.createdByMe === true ? 'me' : undefined}
                  onDropdownVisibleChange={handleCleanSearchInput}
                />
              )}
            </div>
          </div>
        </StyledRow>
        <StyledRow className="ticket-handler border-bottom">
          <div className="flex-spacer" />
          <RangeTimeSwitcher />
          <div className="flex-spacer" />
        </StyledRow>
        <StyledRow className="boards-container">
          {ticketsIsLoading && <Spin className="loading-spinner-overlay" />}
          {ticketsByDay.length > 0 &&
            ticketsByDay.map(day => (
              <RequestsBoard key={day.date} tickets={day.tickets} day={day.date} />
            ))}
        </StyledRow>
      </ContentContainer>
      {/* TODO: New admin modal to create a request */}
      <AddTicketModalAdmin
        title={t('suppliers_router.new_ticket_modal.title')}
        onClose={callback => {
          handleCloseAddTicketModal()
          if (callback) callback()
        }}
        onOk={submitHandler}
        isLoading={isCreateTicketLoading}
        open={openModal === 'add'}
      />
      {/* TODO: New admin modal to update a request */}
      {/* {!!ticketToEdit && ( */}
      <UpdateTicketModalAdmin
        title={t('suppliers_router.new_ticket_modal.title')}
        onClose={callback => {
          handleCloseTicketModal()
          if (callback) {
            callback()
          }
        }}
        onOk={submitHandler}
        isLoading={changeTicketLoading}
      />
      {/* )} */}
      {isDeleteTicketModalOpen && (
        <DeleteTicketModal
          open={isDeleteTicketModalOpen}
          title={t('suppliers_router.delete_ticket_modal.title')}
          onClose={() => setIsDeleteTicketModalOpen(false)}
          ticketId={ticketToEdit}
          isLoading={isDeleteTicketLoading}
        />
      )}
    </>
  )
}

export { Ongoing }
